import React, { useCallback, useState } from 'react';
import { Button, DatePicker, Input, message, Select, Switch } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import moment from 'moment';
import type { Park } from '../../types';
import type { DockingRecord, Participant } from '../../types/docking';
import { generateRecordId } from '../../api/docking';
import { TaskAssignmentEditor } from './TaskAssignmentEditor';

const { TextArea } = Input;

interface DockingRecordFormProps {
  parks: Park[];
  initialRecord?: DockingRecord;
  /** If provided, the park is pre-selected and the selector is disabled */
  fixedParkId?: string;
  onSave: (record: DockingRecord) => void;
  onCancel: () => void;
}

function emptyRecord(parkId?: string): DockingRecord {
  const now = new Date().toISOString();
  return {
    id: generateRecordId(),
    parkId: parkId ?? '',
    dockedAt: moment().format('YYYY-MM-DD'),
    ourParticipants: [{ name: '', title: '' }],
    counterpartyOrg: '',
    counterpartyParticipants: [{ name: '', title: '' }],
    summary: '',
    blockers: '',
    blockerResolved: false,
    followUpPlan: '',
    blockerResolvedAt: null,
    memos: [],
    minutes: [],
    taskAssignments: [],
    maturity: null,
    createdAt: now,
    updatedAt: now,
    archived: false,
  };
}

function ParticipantRows(props: {
  label: string;
  value: Participant[];
  onChange: (val: Participant[]) => void;
}) {
  const { label, value, onChange } = props;
  const update = (idx: number, patch: Partial<Participant>) => {
    const next = [...value];
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };
  const remove = (idx: number) => onChange(value.filter((_, i) => i !== idx));
  const add = () => onChange([...value, { name: '', title: '' }]);

  return (
    <div className="docking-form-field">
      <div className="docking-form-label">{label}</div>
      {value.map((p, i) => (
        <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
          <Input placeholder="姓名" value={p.name} onChange={(e) => update(i, { name: e.target.value })} style={{ flex: 1 }} />
          <Input placeholder="职务" value={p.title} onChange={(e) => update(i, { title: e.target.value })} style={{ flex: 1 }} />
          {value.length > 1 && (
            <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(i)} size="small" />
          )}
        </div>
      ))}
      <Button type="dashed" size="small" icon={<PlusOutlined />} onClick={add} style={{ width: '100%' }}>
        添加人员
      </Button>
    </div>
  );
}

export function DockingRecordForm(props: DockingRecordFormProps): React.ReactElement {
  const { parks, initialRecord, fixedParkId, onSave, onCancel } = props;

  const [record, setRecord] = useState<DockingRecord>(
    initialRecord ? { ...initialRecord } : emptyRecord(fixedParkId),
  );

  const patch = useCallback(
    (p: Partial<DockingRecord>) => setRecord((prev) => ({ ...prev, ...p })),
    [],
  );

  const handleSave = () => {
    if (!record.parkId) { message.warning('请选择关联园区'); return; }
    if (!record.dockedAt) { message.warning('请选择对接时间'); return; }
    if (!record.ourParticipants.some((p) => p.name.trim())) { message.warning('请填写至少一名我方对接人员'); return; }
    if (!record.counterpartyOrg.trim()) { message.warning('请填写对接单位'); return; }
    if (!record.summary.trim()) { message.warning('请填写对接情况'); return; }

    const cleaned: DockingRecord = {
      ...record,
      ourParticipants: record.ourParticipants.filter((p) => p.name.trim()),
      counterpartyParticipants: record.counterpartyParticipants.filter((p) => p.name.trim()),
      updatedAt: new Date().toISOString(),
    };
    onSave(cleaned);
  };

  const parkOptions = parks.map((p) => ({
    label: `${p.name}（${p.parkLevel === 'national' ? '国家级' : p.parkLevel === 'provincial' ? '省级' : '待开发'}）`,
    value: p.id,
  }));

  return (
    <div className="docking-record-form">
      <div className="dash-detail-title">
        {initialRecord ? '编辑对接记录' : '新建对接记录'}
      </div>
      <div className="docking-form-body">
        {/* Park */}
        <div className="docking-form-field">
          <div className="docking-form-label">关联园区 *</div>
          <Select
            showSearch
            optionFilterProp="label"
            placeholder="请搜索并选择园区"
            value={record.parkId || undefined}
            onChange={(v) => patch({ parkId: v })}
            options={parkOptions}
            disabled={!!fixedParkId}
            style={{ width: '100%' }}
            className="dash-select"
            dropdownClassName="dash-select-dropdown"
          />
        </div>

        {/* Date */}
        <div className="docking-form-field">
          <div className="docking-form-label">对接时间 *</div>
          <DatePicker
            value={record.dockedAt ? moment(record.dockedAt) : null}
            onChange={(d) => patch({ dockedAt: d ? d.format('YYYY-MM-DD') : '' })}
            style={{ width: '100%' }}
          />
        </div>

        {/* Maturity */}
        <div className="docking-form-field">
          <div className="docking-form-label">项目成熟度</div>
          <Select
            allowClear
            placeholder="请选择"
            value={record.maturity}
            onChange={(v) => patch({ maturity: v ?? null })}
            style={{ width: '100%' }}
            className="dash-select"
            dropdownClassName="dash-select-dropdown"
            options={[
              { label: '1 星', value: 1 },
              { label: '2 星', value: 2 },
              { label: '3 星', value: 3 },
              { label: '4 星', value: 4 },
              { label: '5 星', value: 5 },
            ]}
          />
        </div>

        {/* Our participants */}
        <ParticipantRows
          label="对接人员（我方）*"
          value={record.ourParticipants}
          onChange={(v) => patch({ ourParticipants: v })}
        />

        {/* Counterparty org */}
        <div className="docking-form-field">
          <div className="docking-form-label">对接单位 *</div>
          <Input
            placeholder="请输入对接单位全称"
            value={record.counterpartyOrg}
            onChange={(e) => patch({ counterpartyOrg: e.target.value })}
          />
        </div>

        {/* Counterparty participants */}
        <ParticipantRows
          label="对接单位参与人员"
          value={record.counterpartyParticipants}
          onChange={(v) => patch({ counterpartyParticipants: v })}
        />

        {/* Summary */}
        <div className="docking-form-field">
          <div className="docking-form-label">对接情况 *</div>
          <TextArea
            rows={4}
            maxLength={2000}
            showCount
            placeholder="请描述本次对接情况"
            value={record.summary}
            onChange={(e) => patch({ summary: e.target.value })}
          />
        </div>

        {/* Blockers */}
        <div className="docking-form-field">
          <div className="docking-form-label">当前工作卡点</div>
          <TextArea
            rows={3}
            placeholder="如有工作卡点请填写"
            value={record.blockers}
            onChange={(e) => patch({ blockers: e.target.value })}
          />
          {record.blockers.trim() && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
              <span style={{ color: 'rgba(255,255,255,0.65)', fontSize: 13 }}>是否已解决：</span>
              <Switch
                checked={record.blockerResolved}
                onChange={(v) =>
                  patch({
                    blockerResolved: v,
                    blockerResolvedAt: v ? moment().format('YYYY-MM-DD') : null,
                  })
                }
                checkedChildren="已解决"
                unCheckedChildren="未解决"
              />
              {record.blockerResolved && (
                <DatePicker
                  size="small"
                  value={record.blockerResolvedAt ? moment(record.blockerResolvedAt) : null}
                  onChange={(d) => patch({ blockerResolvedAt: d ? d.format('YYYY-MM-DD') : null })}
                />
              )}
            </div>
          )}
        </div>

        {/* Follow-up plan */}
        <div className="docking-form-field">
          <div className="docking-form-label">后续工作安排</div>
          <TextArea
            rows={4}
            maxLength={3000}
            showCount
            placeholder="请描述后续工作安排"
            value={record.followUpPlan}
            onChange={(e) => patch({ followUpPlan: e.target.value })}
          />
        </div>

        {/* Memo / Minutes attachment placeholders */}
        <div className="docking-form-field">
          <div className="docking-form-label">备忘录（附件）</div>
          <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 13 }}>
            附件上传功能将在接入后端后启用，当前仅支持记录文字信息。
          </div>
        </div>

        <div className="docking-form-field">
          <div className="docking-form-label">会议纪要（附件）</div>
          <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 13 }}>
            附件上传功能将在接入后端后启用，当前仅支持记录文字信息。
          </div>
        </div>

        {/* Task assignments */}
        <div className="docking-form-field">
          <div className="docking-form-label">工作任务分工</div>
          <TaskAssignmentEditor
            value={record.taskAssignments}
            onChange={(v) => patch({ taskAssignments: v })}
          />
        </div>
      </div>

      {/* Actions */}
      <div className="docking-form-actions">
        <Button onClick={onCancel}>取消</Button>
        <Button type="primary" onClick={handleSave}>保存</Button>
      </div>
    </div>
  );
}
